import MenuIcon from '@mui/icons-material/Menu';
import { Box, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { useLocation, useNavigate, useOutletContext } from 'react-router';

import { BASE, NESTED_ROUTES } from 'common/src/routes/BaseRoutes';
import { generateNestedRoute, isPathActive } from 'common/src/routes/helpers';

import usePageTitle from 'portal/src/navigation/usePageTitle';

import Tabs from './Tabs';

import useAuth from '../../auth/useAuth';
import { IHeaderTab, LayoutContext } from '../LayoutPreference/layoutTypes';
import UserProfilePopup from '../menu/UserProfilePopup';

/** @deprecated Temporary dual-layout rollout new header. Remove when legacy layout is retired. */
const HeaderNew = ({
  tabs = [],
}: {
  title?: string;
  tabs?: IHeaderTab[] | undefined;
}) => {
  const {
    featureChecker,
    isAuthenticated,
    hasAdminAccess,
    hasClientSystemAdminAccess,
    hasReadOnlyAccess,
    hasSystemAdminAccess,
  } = useAuth();
  const navigate = useNavigate();
  const { breakpoints } = useTheme();
  const { setOpenMobile } = useOutletContext<LayoutContext>();
  const isTablet = useMediaQuery(breakpoints.down('lg'));
  const { pathname } = useLocation();

  const supportContactLink = generateNestedRoute(
    BASE.SUPPORT,
    NESTED_ROUTES.SUPPORT.CONTACT_US,
  );

  const isAuthed = isAuthenticated();
  const showTabletMenuButton = isAuthed && isTablet;

  const fallbackTabs = useMemo(() => {
    if (pathname.startsWith(BASE.ALERTS)) {
      return [
        {
          name: 'Alerts',
          path: BASE.ALERTS,
          subTitle: '',
        },
      ];
    }

    if (pathname.startsWith(BASE.CUSTOM_DASHBOARDS)) {
      return [
        {
          name: 'Custom Dashboards',
          path: BASE.CUSTOM_DASHBOARDS,
          subTitle: '',
        },
      ];
    }

    if (pathname.startsWith(BASE.USER_SETTINGS)) {
      return [
        {
          name: 'Profile & Preferences',
          path: BASE.USER_SETTINGS,
          subTitle: '',
        },
      ];
    }

    return [];
  }, [pathname]);

  const tabsToRender = useMemo(() => {
    const filteredTabs = tabs.filter((t) => {
      return (
        !t.shouldRender ||
        t.shouldRender({
          featureChecker,
          hasAdminAccess,
          hasClientSystemAdminAccess,
          hasReadOnlyAccess,
          hasSystemAdminAccess,
        })
      );
    });

    return filteredTabs.length ? filteredTabs : fallbackTabs;
  }, [
    fallbackTabs,
    featureChecker,
    hasAdminAccess,
    hasClientSystemAdminAccess,
    hasReadOnlyAccess,
    hasSystemAdminAccess,
    tabs,
  ]);

  let activeTab = tabsToRender.findIndex((t) => isPathActive(pathname, t.path));

  if (activeTab < 0) {
    activeTab = 0;
  }

  const currentTab = tabsToRender[activeTab];
  const documentTitle = currentTab?.name;

  usePageTitle(documentTitle);

  if (tabsToRender.length === 0 && !showTabletMenuButton) {
    return null;
  }

  return (
    <Box sx={{ backgroundColor: 'background.paper', boxShadow: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        {showTabletMenuButton && (
          <IconButton
            aria-label="menu"
            onClick={() => setOpenMobile(true)}
            sx={{ height: 'auto', m: 0.5 }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Tabs value={activeTab} tabs={tabsToRender} />
        <UserProfilePopup />
      </Box>
    </Box>
  );
};

export default HeaderNew;
